import { getAuth } from "firebase/auth";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import {
  apiClient,
  OccupancyBlocker,
  ParcelDetailsLoaderResult,
} from "~/services/apiClient";
import {
  _ParcelPageTabType,
  FilesReturnType,
  FormattedLeaseData,
  FormattedServiceRequestData,
  NewLeaseFormPayload,
} from "~/types";
import {
  INTENTS,
  resolveActorUserName,
  getFilteredRequestList,
  loadCookiesData,
} from "~/utils";
import { withEntityCacheInvalidation } from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";
import { S3ServiceLambda } from "~/services/s3";

export const ParcelDetailsPageLoader = async ({
  request,
  params,
}: LoaderFunctionArgs): Promise<ParcelDetailsPageLoaderType | Response> => {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) return redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;
  const { searchParams } = new URL(request.url);
  const tab = (searchParams.get("tab") ?? "details") as _ParcelPageTabType;

  const parcelID = params.parcelID || "";

  const result = await apiClient.loader<ParcelDetailsLoaderResult>(
    "parcel-details",
    {
      id: parcelID,
      ownerID,
      ownerType,
      userID: user.uid,
    },
  );

  if (!result.success || !result.data) {
    console.error("Failed to load parcel details:", result.error);
    return redirect("/property");
  }

  const data = result.data;
  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const formattedServiceRequestList = getFilteredRequestList({
    filters: {},
    requestList: data.formattedServiceRequestList as FormattedServiceRequestData[],
    isAdminOrSuperAdmin,
  });

  const tenantList = data.tenantList ?? [];

  const formattedLeaseList = formatParcelLeaseList(
    data.leaseList ?? [],
    tenantList,
    data.parcelDetails,
  );

  const userName = await resolveActorUserName(user, authAccessInfo);

  const files = await S3ServiceLambda.list({
    entityType: APITypes.EntityType.Parcel,
    entityID: parcelID,
  });

  return {
    tab,
    parcelDetails: data.parcelDetails,
    propertyDetails: data.propertyDetails,
    leaseList: data.leaseList,
    formattedLeaseList,
    stays: data.stays ?? [],
    stayRate: data.stayRate ?? null,
    stayBlocks: data.stayBlocks ?? [],
    occupancyLeases: data.occupancyLeases ?? [],
    formattedServiceRequestList,
    tenantList,
    ownerID,
    ownerType,
    userName,
    files: files.data,
    canCreateRequest: data.permissions.canCreateServiceRequest,
    canCreateLease:
      isAdminOrSuperAdmin || data.permissions.canCreateLease,
    canCreateStay: isAdminOrSuperAdmin || data.permissions.canCreateStay,
    canDeleteParcel:
      isAdminOrSuperAdmin || data.permissions.canDeleteParcel,
    canEditParcel: isAdminOrSuperAdmin || data.permissions.canEditParcel,
    canReadParcel: isAdminOrSuperAdmin || data.permissions.canReadParcel,
    isAdminOrSuperAdmin,
    isAuthenticated: true,
    subscriptionPlan: data.subscriptionPlan,
    hasServiceRequestAccess: data.hasServiceRequestAccess,
  };
};

export type ParcelDetailsPageLoaderType = {
  tab: _ParcelPageTabType;
  parcelDetails: APITypes.Parcel;
  propertyDetails: APITypes.Property;
  leaseList: APITypes.Lease[];
  formattedLeaseList: FormattedLeaseData[];
  stays: APITypes.Stay[];
  stayRate: APITypes.StayRate | null;
  stayBlocks: APITypes.StayAvailability[];
  occupancyLeases: APITypes.Lease[];
  formattedServiceRequestList: FormattedServiceRequestData[];
  tenantList: APITypes.Tenant[];
  ownerID: string;
  ownerType: APITypes.OwnerType;
  userName: string;
  files: FilesReturnType;
  canCreateRequest: boolean;
  canCreateLease: boolean;
  canCreateStay: boolean;
  canDeleteParcel: boolean;
  canEditParcel: boolean;
  canReadParcel: boolean;
  isAdminOrSuperAdmin: boolean;
  isAuthenticated: boolean;
  subscriptionPlan: APITypes.SubscriptionPlan;
  hasServiceRequestAccess: boolean;
};

export const ParcelDetailsPageAction = async ({
  request,
  params,
}: ActionFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get("intent");
  const parcelID = params.parcelID || "";
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");

  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) return redirect("/explore");

  const { ownerID, ownerType, role } = authAccessInfo.data;
  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;
  const userName = await resolveActorUserName(user, authAccessInfo);

  switch (intent) {
    case INTENTS._edit_parcel: {
      return withEntityCacheInvalidation(
        [{ entity: "parcel" as const, entityID: parcelID }],
        async () => {
          const payload = JSON.parse(formData.get("body") as string) as Record<
            string,
            unknown
          >;
          const result = await apiClient.sendIntent<APITypes.Parcel>(
            INTENTS._edit_parcel,
            { id: parcelID, ...payload },
          );
          if (result.success && result.data) {
            return { success: true, data: result.data };
          }
          const baseError = result.error ?? "Failed to update parcel";
          const withTrail =
            result.debugLogs?.length
              ? `${baseError} | ${result.debugLogs.join(" | ")}`
              : baseError;
          return {
            success: false,
            data: {} as APITypes.Parcel,
            error: withTrail,
          };
        },
      );
    }
    case INTENTS._remove_parcel: {
      return withEntityCacheInvalidation(
        [
          { entity: "parcel" as const, entityID: parcelID },
          { entity: "property" as const },
        ],
        async () => {
          const body = formData.get("body") as string;
          const payload = body ? (JSON.parse(body) as { _version?: number }) : {};
          const result = await apiClient.sendIntent<{ deleted: boolean; id: string }>(
            INTENTS._remove_parcel,
            {
              id: parcelID,
              _version: payload._version,
              isAdminOrSuperAdmin,
            },
          );
          if (result.success) {
            return redirect("/parcel");
          }
          console.error("Remove parcel failed:", result.error, result.debugLogs);
          return {
            success: false,
            error: result.error ?? "Failed to delete parcel",
            ...(Array.isArray(result.debugLogs) && {
              debugLogs: result.debugLogs,
            }),
          };
        },
      );
    }
    case INTENTS._add_lease: {
      return withEntityCacheInvalidation(
        [
          { entity: "parcel" as const, entityID: parcelID },
          { entity: "lease" as const },
        ],
        async () => {
          const payload = JSON.parse(
            formData.get("body") as string,
          ) as NewLeaseFormPayload;
          const result = await apiClient.sendIntent<APITypes.Lease>(
            INTENTS._add_lease,
            {
              ...payload,
              entityID: parcelID,
              entityType: APITypes.EntityType.Parcel,
              ownerID,
              ownerType,
            },
          );
          if (result.success && result.data) {
            return { success: true, data: result.data };
          }
          return {
            success: false,
            data: {} as APITypes.Lease,
            error: result.error ?? "Failed to create lease",
          };
        },
      );
    }
    case INTENTS._add_stay: {
      return withEntityCacheInvalidation(
        [{ entity: "parcel" as const, entityID: parcelID }],
        async () => {
          const payload = JSON.parse(formData.get("body") as string) as {
            tenantID?: string;
            checkIn?: string;
            checkOut?: string;
            notes?: string;
            nightlyRate?: number;
            totalAmount?: number;
            newGuest?: {
              firstName?: string;
              lastName?: string;
              email?: string;
              phoneNumber?: string;
            };
          };
          let tenantID = payload.tenantID?.trim() ?? "";
          if (!tenantID && payload.newGuest) {
            const guest = payload.newGuest;
            const tenantResult = await apiClient.sendIntent<APITypes.Tenant>(
              INTENTS._add_tenant,
              {
                type: APITypes.TenantType.ShadowTenant,
                firstName: guest.firstName,
                lastName: guest.lastName,
                email: guest.email,
                phoneNumber: guest.phoneNumber,
              },
            );
            if (!tenantResult.success || !tenantResult.data?.id) {
              return {
                success: false,
                error: tenantResult.error ?? "Failed to create guest tenant",
              };
            }
            tenantID = tenantResult.data.id;
          }
          const result = await apiClient.sendIntent<APITypes.Stay>(
            INTENTS._add_stay,
            {
              entityType: APITypes.EntityType.Parcel,
              entityID: parcelID,
              tenantID,
              checkIn: payload.checkIn,
              checkOut: payload.checkOut,
              notes: payload.notes,
              nightlyRate: payload.nightlyRate,
              totalAmount: payload.totalAmount,
              status: APITypes.StayStatusType.Confirmed,
              ownerID,
              ownerType,
            },
          );
          if (result.success && result.data) {
            return { success: true, data: result.data };
          }
          return {
            success: false,
            error: result.error ?? "Failed to create stay",
            ...(typeof result.code === "string" && { code: result.code }),
            ...(Array.isArray(result.blockers) && { blockers: result.blockers }),
          };
        },
      );
    }
    case INTENTS._update_stay: {
      return withEntityCacheInvalidation(
        [{ entity: "parcel" as const, entityID: parcelID }],
        async () => {
          const payload = JSON.parse(formData.get("body") as string) as Record<
            string,
            unknown
          >;
          const result = await apiClient.sendIntent<APITypes.Stay>(
            INTENTS._update_stay,
            payload,
          );
          if (result.success && result.data) {
            return { success: true, data: result.data };
          }
          return stayIntentFailure(result, "Failed to update stay");
        },
      );
    }
    case INTENTS._request_stay_payment: {
      return withEntityCacheInvalidation(
        [{ entity: "parcel" as const, entityID: parcelID }],
        async () => {
          const payload = JSON.parse(formData.get("body") as string) as Record<
            string,
            unknown
          >;
          const result = await apiClient.sendIntent<APITypes.Transaction>(
            INTENTS._request_stay_payment,
            payload,
          );
          if (result.success && result.data) {
            return { success: true, data: result.data };
          }
          return stayIntentFailure(result, "Failed to request stay payment");
        },
      );
    }
    case INTENTS._upsert_stay_rate: {
      return withEntityCacheInvalidation(
        [{ entity: "parcel" as const, entityID: parcelID }],
        async () => {
          const payload = JSON.parse(formData.get("body") as string) as Record<
            string,
            unknown
          >;
          const result = await apiClient.sendIntent<APITypes.StayRate>(
            INTENTS._upsert_stay_rate,
            {
              entityType: APITypes.EntityType.Parcel,
              entityID: parcelID,
              ownerID,
              ownerType,
              ...payload,
            },
          );
          if (result.success && result.data) {
            return { success: true, data: result.data };
          }
          return stayIntentFailure(result, "Failed to save stay rate");
        },
      );
    }
    case INTENTS._add_stay_availability: {
      return withEntityCacheInvalidation(
        [{ entity: "parcel" as const, entityID: parcelID }],
        async () => {
          const payload = JSON.parse(formData.get("body") as string) as Record<
            string,
            unknown
          >;
          const result = await apiClient.sendIntent<APITypes.StayAvailability>(
            INTENTS._add_stay_availability,
            {
              entityType: APITypes.EntityType.Parcel,
              entityID: parcelID,
              ownerID,
              ownerType,
              ...payload,
            },
          );
          if (result.success && result.data) {
            return { success: true, data: result.data };
          }
          return stayIntentFailure(result, "Failed to add blocked dates");
        },
      );
    }
    case INTENTS._remove_stay_availability: {
      return withEntityCacheInvalidation(
        [{ entity: "parcel" as const, entityID: parcelID }],
        async () => {
          const payload = JSON.parse(formData.get("body") as string) as Record<
            string,
            unknown
          >;
          const result = await apiClient.sendIntent<{ id: string }>(
            INTENTS._remove_stay_availability,
            payload,
          );
          if (result.success) {
            return { success: true, data: result.data };
          }
          return stayIntentFailure(result, "Failed to remove blocked dates");
        },
      );
    }
    case INTENTS._add_service_request: {
      const payload = JSON.parse(
        formData.get("body") as string,
      ) as Record<string, unknown>;

      const serviceRequestPayload = {
        ...payload,
        creatorID: user?.uid ?? "",
        creatorName: userName,
        ownerID,
        ownerType,
        entityType: APITypes.EntityType.Parcel,
        entityID: parcelID,
      };

      return withEntityCacheInvalidation(
        [{ entity: "serviceRequest" }],
        async () => {
          const result = await apiClient.sendIntent(
            INTENTS._add_service_request,
            serviceRequestPayload,
          );
          return result.success
            ? { success: true, data: result.data }
            : { success: false, error: result.error };
        },
      );
    }
    default:
      return { success: false, error: "Unknown intent" };
  }
};

function unresolvedTenant(tenantID: string): APITypes.Tenant {
  return {
    __typename: "Tenant",
    id: tenantID,
    firstName: "",
    lastName: "",
    createdAt: "",
    updatedAt: "",
    _version: 0,
    _lastChangedAt: 0,
  } as APITypes.Tenant;
}

function formatParcelLeaseList(
  leases: APITypes.Lease[],
  tenants: APITypes.Tenant[],
  parcel: APITypes.Parcel,
): FormattedLeaseData[] {
  const tenantById = new Map(tenants.map((tenant) => [tenant.id, tenant]));
  return leases.map((lease) => ({
    leaseInfo: lease,
    tenantInfo:
      tenantById.get(lease.tenantID ?? "") ??
      unresolvedTenant(lease.tenantID ?? ""),
    entityType: APITypes.EntityType.Parcel,
    entityDetails: parcel,
    advanceLeaseAction: "_Review_Application",
    cancelLeaseAction: "_Deny_Application",
  }));
}

function stayIntentFailure(
  result: {
    error?: string;
    debugLogs?: string[];
    code?: string;
    blockers?: OccupancyBlocker[];
  },
  fallback: string,
) {
  return {
    success: false as const,
    error: result.error ?? fallback,
    ...(Array.isArray(result.debugLogs) && { debugLogs: result.debugLogs }),
    ...(typeof result.code === "string" && { code: result.code }),
    ...(Array.isArray(result.blockers) && { blockers: result.blockers }),
  };
}
